import extensions from "_src/engine/extensions";
import output from "_src/engine/output.js";
import GL from "_src/engine/gl";
import {drawMode, drawQueryState, memoryType} from "_src/engine/constants";
import memoryProfiler from "_src/engine/memory_profiler";
import appState from "_src/app_state";
import config from "_src/config";

const tmpArray = [];

export class ChunkInDrawable {
  constructor() {
    this.bbox = new Array(6);
    this.inFrustum = false;

    this.queryState = drawQueryState.INACTIVE;
    this.queryResult = true;
    this.drawQuery = null;

    this.attributesMap = [];
    this.activeShaderProgram = null;

    this.instancedAttributes = new Map();
  }
}

export default class Chunk {

  constructor(id = "") {

    this.ibo = null;

    this._drawMode = null;
    this._indicesType = null;

    this._numElems = 0;

    this.enableFrustumTest = true;

    this.geometry = null;
    this.color = new Float32Array([1.0, 1.0, 1.0]);

    this.id = id;

    this.attributeBuffers = {};

    this.needsReload = false;

    this.dupliVertices = {};
    this.parentVerticesMask = null;

    this.setDrawMode(drawMode.TRIANGLES);

    this._dirtyAttributes = new Set();

    this._memoryUsageReportOwners = new Set();
  }

  updateGlBuffers(shader) {

    if (!this._updateIBO()) {
      return false;
    }

    if (!this._updateBuffers(shader)) {
      return false;
    }

    return true;

  }

  getID() {
    return this.id;
  }

  _updateIBO() {

    if (this.geometry.isPointCloud) {
      return true;
    }

    if (!this._dirtyAttributes.has("indices")) {
      return true;
    }

    const indices = this.geometry.getItemBuffer("indices");

    // NOTE: we can build acceleration structures in workers
    if (indices == null) {
      return false;
    }

    const gl = GL.getContext();

    if (!this.ibo) {
      this.ibo = gl.createBuffer();
    }

    this._numElems = indices.length;

    this._indicesType = getIndicesType(indices);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
    memoryProfiler.markMemoryAllocated(this.ibo, memoryType.GPU_GEOMETRY, indices.byteLength);
    for (const memoryUsageReportOwner of this._memoryUsageReportOwners) {
      memoryProfiler.markMemoryAllocated(this.ibo, memoryType.GPU_GEOMETRY, indices.byteLength, memoryUsageReportOwner);
    }

    this._dirtyAttributes.delete("indices");

    const layout = this.geometry.getLayout();
    if (layout["indices"].shouldBeUnloaded()) {
      memoryProfiler.markMemoryReleased(layout["indices"].buffer);
      delete layout["indices"];
    }

    return true;

  }

  markAttributeDirty(attName) {
    this._dirtyAttributes.add(attName);
  }

  cleanup() {

    this.cleanupGLBuffers();

    if (this.geometry) {
      this.geometry.releaseMemory();
      this.geometry.terminateDataBuilding();
      this.geometry = null;
    }

    this._numElems = 0;

    this.parentVerticesMask = null;

    this._dirtyAttributes.clear();

  }

  cleanupGLBuffers() {

    this.reportMemoryRelease();

    const gl = GL.getContext();
    for (let attrName in this.attributeBuffers) {
      const attrib = this.attributeBuffers[attrName];
      gl.deleteBuffer(attrib.vbo);
    }

    if (this.ibo) {
      gl.deleteBuffer(this.ibo);
    }

    this.ibo = null;
    this.attributeBuffers = {};

  }

  getVerticesCount() {
    let vertCount = this.geometry.getVerticesCount();
    if (vertCount === 0) {
      const positionsAttribute = this.attributeBuffers["a_position"];
      vertCount = positionsAttribute.byteLength / positionsAttribute.size;
    }

    return vertCount;
  }

  isEmpty () {
    return !this.geometry || this.geometry.empty();
  }

  hasDrawElements() {
    return this._numElems !== 0;
  }

  hasSelection() {

    const selectionMask = this.geometry.getItemBuffer("a_selection_mask");

    if (!selectionMask) {
      return false;
    }

    for (let i = 0; i < selectionMask.length; ++i) {
      if (selectionMask[i]) {
        return true;
      }
    }

    return false;

  }

  draw(gl) {
    if (this.geometry.isPointCloud) {
        gl.drawArrays(this._drawMode, 0, this._numElems);
    } else {
      gl.drawElements(this._drawMode, this._numElems, this._indicesType, 0);
    }
  }

  drawInstanced(instanceExt, instanceCount) {
    instanceExt.drawElementsInstanced(this._drawMode, this._numElems, this._indicesType, 0, instanceCount);
  }

  setDrawMode(mode) {
    this._drawMode = GL.getGLDrawMode(mode);
  }

  addVertexDuplicates(vertexInd, dupliChunk, dupliVertInd) {

    const dupliList = this.dupliVertices;

    let dupliVerts = dupliList[vertexInd];

    if (!dupliVerts) {
      dupliList[vertexInd] = [];
    }

    dupliList[vertexInd].push(dupliChunk, dupliVertInd);
  }

  setHasParentVertex(index, value) {
    this.parentVerticesMask[index] = value;
  }

  resetDupliVertices() {
    this.dupliVertices = {};
  }

  resetParentVerticesMask() {
    this.parentVerticesMask = new Uint8Array(this.geometry.getVerticesCount());
  }

  clearParentVerticesMask() {
    this.parentVerticesMask = null;
  }

  attachBuffers(gl, attributesMap) {

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);

    for (let i = 0; i < attributesMap.length; i += 2) {

      const ind = attributesMap[i];
      const attr = attributesMap[i + 1];

      gl.bindBuffer(gl.ARRAY_BUFFER, attr.vbo);
      gl.enableVertexAttribArray(ind);
      gl.vertexAttribPointer(ind, attr.size, attr.type, attr.normalize,
                          attr.stride, attr.offset);

    }

  }

  detachBuffers(gl, attributesMap) {
    for (let j = 0; j < attributesMap.length; j+=2) {
      const ind = attributesMap[j];
      gl.disableVertexAttribArray(ind);
    }
  }

  detachInstancedBuffers(gl, instanceExt, attributesMap) {
    for (let j = 0; j < attributesMap.length; j+=2) {
      const ind = attributesMap[j];
      gl.disableVertexAttribArray(ind);
      instanceExt.vertexAttribDivisor(ind, 0);
    }
  }

  attachInstancedBuffers(gl, instanceExt, attributesMap, instanceDivisor) {

    for (let i = 0; i < attributesMap.length; i += 2) {
      const ind = attributesMap[i];
      const attr = attributesMap[i + 1];

      gl.bindBuffer(gl.ARRAY_BUFFER, attr.vbo);
      gl.enableVertexAttribArray(ind);
      gl.vertexAttribPointer(ind, attr.size, attr.type, attr.normalize,
        attr.stride, attr.offset);

      instanceExt.vertexAttribDivisor(ind, instanceDivisor);
    }

  }

  requestMemoryUsageReport(reportOwner) {
    this._memoryUsageReportOwners.add(reportOwner);
    this._reportMemoryUsage(reportOwner);
  }

  _reportMemoryUsage(reportOwner) {
    for (const attrName in this.attributeBuffers) {
      const attrib = this.attributeBuffers[attrName];
      memoryProfiler.markMemoryAllocated(attrib.vbo, memoryType.GPU_GEOMETRY, attrib.byteLength, reportOwner);
    }

    const indices = this.geometry ? this.geometry.getItemBuffer("indices") : null;
    if (this.ibo && indices) {
      memoryProfiler.markMemoryAllocated(this.ibo, memoryType.GPU_GEOMETRY, indices.byteLength, reportOwner);
    }
  }

  reportMemoryReleaseFromOwner(reportOwner) {
    this._memoryUsageReportOwners.delete(reportOwner);
    this._reportMemoryReleaseFromOwner(reportOwner);
  }

  reportMemoryRelease() {
    for (const memoryUsageReportOwner of this._memoryUsageReportOwners){
      this._reportMemoryReleaseFromOwner(memoryUsageReportOwner);
    }
    this.reportMemoryReleaseFromOwner();

    this._memoryUsageReportOwners.clear();
  }

  _reportMemoryReleaseFromOwner(reportOwner) {
    for (const attrName in this.attributeBuffers) {
      const attrib = this.attributeBuffers[attrName];
      memoryProfiler.markMemoryReleased(attrib.vbo, reportOwner);
    }

    if (this.ibo) {
      memoryProfiler.markMemoryReleased(this.ibo, reportOwner);
    }
  }

  _updateBuffers(shader) {

    if (this._dirtyAttributes.size === 0) {
      return true;
    }

    const availableGeometryAttrs = this.geometry.getLayout();
    const buffersToUnload = tmpArray;

    for (const geomAttrName in availableGeometryAttrs) {
      if (!this._dirtyAttributes.has(geomAttrName)) {
        continue;
      }

      let shaderAttrName = geomAttrName;
      for (const [shaderName, geomName] of Object.entries(shader.instancedAttributesRemapping)) {
        if (geomAttrName === geomName) {
          shaderAttrName = shaderName;
          break;
        }
      }

      if (!config.viewOnlyMode && !(shaderAttrName in shader.attributesLocations)) {
        continue;
      }

      const layoutItem = availableGeometryAttrs[geomAttrName];
      const buffer = layoutItem.getBuffer();
      if (!buffer) {
        return false;
      }

      if (this.geometry.isPointCloud && geomAttrName === "a_position") {
        const positionsElemSize = layoutItem.getComponentsCount();
        this._numElems = buffer.length / positionsElemSize;
      }

      const attrib = this.attributeBuffers[geomAttrName];

      if (attrib) {
        writeAttribute(attrib, buffer);
      } else {
        const normalizationRequired = geomAttrName === "a_color";
        this.attributeBuffers[geomAttrName] = createAttribute(layoutItem, buffer, normalizationRequired);
      }
      memoryProfiler.markMemoryAllocated(this.attributeBuffers[geomAttrName].vbo, memoryType.GPU_GEOMETRY, this.attributeBuffers[geomAttrName].byteLength);
      for (const memoryUsageReportOwner of this._memoryUsageReportOwners) {
        memoryProfiler.markMemoryAllocated(this.attributeBuffers[geomAttrName].vbo, memoryType.GPU_GEOMETRY, this.attributeBuffers[geomAttrName].byteLength, memoryUsageReportOwner);
      }

      this._dirtyAttributes.delete(geomAttrName);

      if (layoutItem.shouldBeUnloaded()) {
        buffersToUnload.push(geomAttrName);
      }

    }

    for (const bufferToUnload of buffersToUnload) {
      memoryProfiler.markMemoryReleased(availableGeometryAttrs[bufferToUnload].buffer);
      delete availableGeometryAttrs[bufferToUnload];
    }
    buffersToUnload.length = 0;

    return true;

  }

  attributeChanged(attrName) {
    return this._dirtyAttributes.has(attrName)
  }

  async buildNormalsAndBorder(useWorker, withNormals, withBorder) {
    await this.geometry.buildNormalsAndBorder(useWorker, withNormals, withBorder);
    this.markAttributeDirty("a_normal");
    if (config.viewOnlyMode) {
      //force to update these buffers and unload them further
      this.markAttributeDirty("a_position");
      this.markAttributeDirty("indices");
    }
  }

  async invertNormals(useWorker) {
    await this.geometry.invertNormals(useWorker);
    if (this.geometry.getLayoutItem("indices")) {
      this.markAttributeDirty("indices");
    }
    if (this.geometry.getLayoutItem("a_normal")) {
      this.markAttributeDirty("a_normal");
    }
  }

  updateSelectionMask(needsSelection = appState.getState(appState.GS_OCTREE) || appState.getState(appState.GS_HALF_EDGE)) {
    const selectionMaskExists = this.geometry.hasLayoutItem("a_selection_mask");
    const needsInitSelectionMask = !selectionMaskExists && needsSelection;
    const needsRemoveSelectionMask = selectionMaskExists && !needsSelection;

    if (needsInitSelectionMask) {
      this.geometry.initSelectionBuffers();
      this.markAttributeDirty("a_selection_mask");
    }

    if (needsRemoveSelectionMask) {
      this.geometry.removeSelectionBuffers();
      this.markAttributeDirty("a_selection_mask");
    }
  }

  removeNormals() {
    this.geometry.removeNormals();
    this.markAttributeDirty("a_normal");
  }

  updateVertexID(needsVertexID) {

    const needsUpdate = needsVertexID && !this.geometry.hasLayoutItem("a_vertex_id") ||
      !needsVertexID && this.geometry.hasLayoutItem("a_vertex_id");

    if (!needsUpdate) {
      return;
    }

    if (needsVertexID) {
      this.geometry.initVertexID(this.getVerticesCount());
    } else {
      this.geometry.deleteVertexID();
    }

    this.markAttributeDirty("a_vertex_id");

  }

  averageNormals(dupliVertices, vertexMask = null) {
    const normals = this.geometry.getLayoutItem("a_normal").getBuffer();

    let averagedNormalX = 0;
    let averagedNormalY = 0;
    let averagedNormalZ = 0;

    for (const [vertexIndex, dupli] of Object.entries(dupliVertices)) {
      if (vertexMask && !vertexMask[vertexIndex]) {
        continue;
      }

      averagedNormalX = normals[3*vertexIndex];
      averagedNormalY = normals[3*vertexIndex+1];
      averagedNormalZ = normals[3*vertexIndex+2];

      for (let i = 0; i < dupli.length; i+=2) {
        const dupliChunk = dupli[i];
        const dupliIndex = dupli[i+1];
        const dupliNormals = dupliChunk.geometry.getLayoutItem("a_normal").getBuffer();

        averagedNormalX += dupliNormals[3*dupliIndex];
        averagedNormalY += dupliNormals[3*dupliIndex+1];
        averagedNormalZ += dupliNormals[3*dupliIndex+2];
      }

      const sqrtLen = Math.sqrt(averagedNormalX * averagedNormalX + averagedNormalY * averagedNormalY + averagedNormalZ * averagedNormalZ);
      averagedNormalX /= sqrtLen;
      averagedNormalY /= sqrtLen;
      averagedNormalZ /= sqrtLen;

      normals[3*vertexIndex] = averagedNormalX;
      normals[3*vertexIndex+1] = averagedNormalY;
      normals[3*vertexIndex+2] = averagedNormalZ;

      for (let i = 0; i < dupli.length; i+=2) {
        const dupliChunk = dupli[i];
        const dupliIndex = dupli[i+1];
        const dupliNormals = dupliChunk.geometry.getLayoutItem("a_normal").getBuffer();

        dupliNormals[3*dupliIndex] = averagedNormalX;
        dupliNormals[3*dupliIndex+1] = averagedNormalY;
        dupliNormals[3*dupliIndex+2] = averagedNormalZ;

        dupliChunk.markAttributeDirty("a_normal");
      }

      if (vertexMask && vertexMask[vertexIndex] === 2) vertexMask[vertexIndex] = 0;
    }

    //TODO: WS-1494

    this.markAttributeDirty("a_normal");
  }

}

function writeAttribute(attribute, data) {

  const gl = GL.getContext();

  gl.bindBuffer(gl.ARRAY_BUFFER, attribute.vbo);

  // TODO: need to update all attributes offsets if they share the same vbo
  if (attribute.byteLength === data.byteLength) {
    gl.bufferSubData(gl.ARRAY_BUFFER, attribute.offset, data);
  } else {
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
    attribute.byteLength = data.byteLength;
  }

}

function createAttribute(layoutItem, buffer, normalize) {

  const size = layoutItem.getComponentsCount();
  const offset = layoutItem.getByteOffset();
  const stride = layoutItem.getStride();
  const divisor = layoutItem.getDivisor();

  const type = GL.getGLBufferType(layoutItem.type);
  const byteLength = buffer.byteLength;

  const vbo = createVBO(buffer);

  return {
    vbo,
    size,
    type,
    normalize,
    stride,
    offset,
    divisor,
    byteLength
  };

}

function createVBO(attData) {

  const gl = GL.getContext();
  const vbo = gl.createBuffer();

  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, attData, gl.DYNAMIC_DRAW);

  return vbo;

}

function getIndicesType(indices) {

  const gl = GL.getContext();

  if (indices instanceof Uint32Array) {

    const ext = extensions.get("OES_element_index_uint");

    if (!ext) {

      output.error(output.INITIALIZATION_ERROR, "\"OES_element_index_uint\" is not supported! Can't draw the geometry.");
      return null;

    }

    return gl.UNSIGNED_INT;

  } else if (indices instanceof Uint16Array) {

    return gl.UNSIGNED_SHORT;

  } else {

    return gl.UNSIGNED_BYTE;

  }
}
